import React, { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth, effectiveTier } from "@/lib/AuthContext";
import DashboardShell from "@/components/dashboard/DashboardShell";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Check, ArrowRight, Loader2, Coins, Mic, MicOff, Repeat, Zap, Crown, Lock } from "lucide-react";

// Credit grants mirror backend SUB_CREDIT_GRANT — keep in sync with
// /api/credits/pricing > membership_grants. Hybrid model: base + bonus.
const TIERS = [
  {
    id: "weekly", name: "Weekly", price: 15, cadence: "/week",
    tagline: "Try the system. Cancel anytime.",
    grant: { base: 300, bonus:  40, total:  340 },
    creates: "≈ 21 standard videos · 113 images · or 85 voiceovers (1 min each)",
    voiceover: { allowed: true, note: "Credit-debited from your wallet" },
    features: [
      "340 credits / week (300 base + 40 bonus)",
      "Pay-as-you-go top-ups always available",
      "1080p export · no watermark",
      "Generations debit your credit wallet",
      "Voiceover unlocked",
      "Cancel anytime",
    ],
  },
  {
    id: "starter", name: "Starter", price: 29.99, cadence: "/month",
    tagline: "For solo creators and small businesses.",
    grant: { base: 600, bonus: 120, total:  720 },
    creates: "≈ 45 standard videos · 240 images · or 180 voiceovers (1 min each)",
    voiceover: { allowed: true, note: "Credit-debited from your wallet" },
    features: [
      "720 credits / month (600 base + 120 bonus)",
      "1080p export · no watermark",
      "Generations debit your credit wallet",
      "Voiceover unlocked",
      "Personal commercial-use license",
      "Top-up credits stack on top",
    ],
  },
  {
    id: "pro", name: "Pro", price: 59.99, cadence: "/month",
    tagline: "For high-volume marketers and agencies.",
    highlighted: true, badge: "Most Popular",
    grant: { base: 1200, bonus: 300, total: 1500 },
    creates: "≈ 93 standard videos · 500 images · or 375 voiceovers (1 min each)",
    voiceover: { allowed: true, note: "Priority TTS queue · credit-debited" },
    features: [
      "1,500 credits / month (1,200 base + 300 bonus)",
      "Priority render queue · ~2–3× faster",
      "Music promo presets + audio",
      "Brand kit (logo + colors)",
      "Voiceover · priority queue",
      "Full commercial license",
    ],
  },
  {
    id: "premium", name: "Premium", price: 99.99, cadence: "/month",
    tagline: "For production houses and operators.",
    grant: { base: 2000, bonus: 700, total: 2700 },
    creates: "≈ 75 cinema videos (10s) · 540 images · or 675 voiceovers (1 min each)",
    voiceover: { allowed: true, note: "ElevenLabs Cinema voice · highest fidelity" },
    features: [
      "2,700 credits / month (2,000 base + 700 bonus)",
      "Highest quality (Sora 2 Pro · Cinema tier)",
      "Express render queue · top priority",
      "3 team seats included",
      "Voiceover · ElevenLabs Cinema tier",
      "White-label exports",
    ],
  },
];

const FREE_TRIAL = {
  voiceover: { allowed: false, note: "Locked — upgrade any plan to unlock voice" },
  features: [
    "2 watermarked downloads (lifetime)",
    "Sora 2 video generation",
    "Image generation (Nano Banana)",
    "NO voiceover (paid plans only)",
  ],
};

export default function PricingPage() {
  const { user, loading } = useAuth();
  const [busyTier, setBusyTier] = useState(null);
  const [busyPack, setBusyPack] = useState(null);
  const [error, setError] = useState("");
  const [usage, setUsage] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [view, setView] = useState("memberships"); // 'memberships' | 'payg'

  useEffect(() => {
    if (!user) return;
    api.get("/billing/me/usage").then(({ data }) => setUsage(data)).catch(() => {});
    api.get("/credits/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, [user]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/pricing" replace />;

  const currentTier = effectiveTier(user);

  const onSubscribe = async (tierId) => {
    setError(""); setBusyTier(tierId);
    try {
      const { data } = await api.post("/stripe/checkout", { tier: tierId });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusyTier(null);
    }
  };

  const onBuyPack = async (sku) => {
    setError(""); setBusyPack(sku);
    try {
      const { data } = await api.post("/credits/checkout", { sku });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusyPack(null);
    }
  };

  const onOpenPortal = async () => {
    setError(""); setCancelling(true);
    try {
      const { data } = await api.post("/stripe/portal");
      window.location.href = data.portal_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setCancelling(false);
    }
  };

  return (
    <DashboardShell>
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-3">Plans & Credits</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Choose how you pay.</h1>
        <p className="text-[#A0A0A0] mt-4 max-w-2xl">
          Two ways to keep creating: <span className="text-[#F4F4F0]">recurring memberships</span> with included downloads + voiceover, or <span className="text-[#F4F4F0]">pay-as-you-go credit packs</span> with no commitment.
        </p>
      </div>

      {usage && (
        <CurrentPlanCard user={user} usage={usage} onOpenPortal={onOpenPortal} cancelling={cancelling} />
      )}

      {error && (
        <div className="mb-6 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="pricing-error">{error}</div>
      )}

      {/* Side-by-side decision strip — speaks to the buyer's intent first */}
      <div className="mb-8 grid md:grid-cols-2 gap-4" data-testid="pricing-decision-strip">
        <button
          onClick={() => setView("payg")}
          data-testid="decision-payg"
          className={`text-left p-5 md:p-6 border transition-colors ${view === "payg" ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] hover:border-[#D4AF37]/40 bg-[#0F0F0F]"}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Zap className={`w-4 h-4 ${view === "payg" ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`} />
            <p className={`text-[10px] uppercase tracking-[0.22em] font-bold ${view === "payg" ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`}>Pay-As-You-Go</p>
          </div>
          <h3 className="font-display text-lg md:text-xl font-extrabold text-[#F4F4F0] mb-1">Just need one project?</h3>
          <p className="text-sm text-[#A0A0A0]">Pay as you go. Buy a credit pack, render what you need, walk away. Credits never expire.</p>
        </button>
        <button
          onClick={() => setView("memberships")}
          data-testid="decision-memberships"
          className={`text-left p-5 md:p-6 border transition-colors ${view === "memberships" ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#2A2A2A] hover:border-[#D4AF37]/40 bg-[#0F0F0F]"}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Repeat className={`w-4 h-4 ${view === "memberships" ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`} />
            <p className={`text-[10px] uppercase tracking-[0.22em] font-bold ${view === "memberships" ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`}>Membership</p>
          </div>
          <h3 className="font-display text-lg md:text-xl font-extrabold text-[#F4F4F0] mb-1">Creating often?</h3>
          <p className="text-sm text-[#A0A0A0]">Membership gives you more downloads, voice, and speed. Auto-renews, cancel anytime.</p>
        </button>
      </div>

      {/* Toggle */}
      <div className="mb-8 inline-flex border border-[#2A2A2A] bg-[#0F0F0F]" data-testid="pricing-toggle">
        <button
          onClick={() => setView("memberships")}
          data-testid="toggle-memberships"
          className={`px-5 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 ${view === "memberships" ? "bg-[#D4AF37] text-[#080808] font-bold" : "text-[#A0A0A0]"}`}
        >
          <Repeat className="w-3.5 h-3.5" /> Memberships
        </button>
        <button
          onClick={() => setView("payg")}
          data-testid="toggle-payg"
          className={`px-5 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 ${view === "payg" ? "bg-[#D4AF37] text-[#080808] font-bold" : "text-[#A0A0A0]"}`}
        >
          <Zap className="w-3.5 h-3.5" /> Pay-As-You-Go
        </button>
      </div>

      {view === "memberships" ? (
        <MembershipsView TIERS={TIERS} FREE_TRIAL={FREE_TRIAL} currentTier={currentTier}
                         busyTier={busyTier} onSubscribe={onSubscribe} />
      ) : (
        <PaygView pricing={pricing} busyPack={busyPack} onBuyPack={onBuyPack} />
      )}

      <p className="mt-10 text-center text-xs text-[#A0A0A0]">
        Test mode active. Use card <span className="font-mono text-[#F4F4F0]">4242 4242 4242 4242</span>, any future date, any CVC.
        <br />Questions? <a href="mailto:support@ybmstudios.com?subject=YBM%20Studios%20Billing" data-testid="dash-pricing-support-email" className="text-[#D4AF37] hover:underline">support@ybmstudios.com</a>
      </p>
    </DashboardShell>
  );
}

/* ---------- Memberships ---------- */
function MembershipsView({ TIERS, FREE_TRIAL, currentTier, busyTier, onSubscribe }) {
  return (
    <>
      {/* Free-trial card */}
      <div className="mb-5 border border-[#2A2A2A] bg-[#0F0F0F] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5" data-testid="free-trial-card">
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] mb-1">Free Trial · You start here</p>
          <h3 className="font-display text-2xl font-extrabold text-[#F4F4F0] mb-2">2 watermarked downloads</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1.5 text-xs text-[#A0A0A0]">
            {FREE_TRIAL.features.map((f) => (
              <li key={f} className="flex items-start gap-2"><Check className="w-3 h-3 text-[#D4AF37] mt-0.5" /> {f}</li>
            ))}
          </ul>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 border border-amber-500/30 bg-amber-500/5">
          <Lock className="w-4 h-4 text-amber-300" />
          <div>
            <p className="text-xs text-amber-200 font-semibold">No voiceover on Free Trial</p>
            <p className="text-[10px] text-amber-200/70">Unlock by upgrading any plan below.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-testid="memberships-grid">
        {TIERS.map((t) => {
          const isCurrent = currentTier === t.id;
          return (
            <div
              key={t.id}
              data-testid={`pricing-card-${t.id}`}
              className={`relative flex flex-col p-7 ${t.highlighted ? "bg-[#121212] border border-[#D4AF37] pro-card-glow lg:-translate-y-2" : "bg-[#121212] border border-[#2A2A2A]"}`}
            >
              {t.badge && (
                <span className="absolute -top-3 left-7 bg-[#D4AF37] text-[#080808] text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1.5">
                  {t.badge}
                </span>
              )}
              <h3 className="font-display text-2xl md:text-3xl font-extrabold text-[#F4F4F0]">{t.name}</h3>
              <p className="mt-2 text-sm text-[#A0A0A0] min-h-[44px]">{t.tagline}</p>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-xl text-[#A0A0A0]">$</span>
                <span className="font-display text-5xl font-black tracking-tighter">{t.price}</span>
                <span className="text-xs text-[#A0A0A0] ml-1">{t.cadence}</span>
              </div>

              {/* Credit grant chip — the centerpiece of hybrid pricing */}
              {t.grant && (
                <div className="mt-4 px-3 py-2.5 border border-[#D4AF37]/40 bg-[#D4AF37]/5" data-testid={`grant-chip-${t.id}`}>
                  <div className="flex items-baseline gap-2">
                    <Coins className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="font-display text-lg font-bold text-[#F4F4F0]">{t.grant.total.toLocaleString()}</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">credits total</span>
                  </div>
                  <p className="text-[10px] text-[#D4AF37] mt-0.5">
                    {t.grant.base.toLocaleString()} base <span className="text-[#5A5A5A]">+</span> +{t.grant.bonus.toLocaleString()} bonus
                  </p>
                  {t.creates && <p className="text-[10px] text-[#A0A0A0] mt-1.5 leading-relaxed">{t.creates}</p>}
                </div>
              )}

              {/* Voiceover badge */}
              <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider border ${t.voiceover.allowed ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" : "border-red-500/40 text-red-300 bg-red-500/5"}`}>
                {t.voiceover.allowed ? <Mic className="w-3 h-3" /> : <MicOff className="w-3 h-3" />}
                {t.voiceover.allowed ? "Voiceover unlocked" : "Voiceover locked"}
              </div>
              <p className="text-[10px] text-[#A0A0A0] mt-2">{t.voiceover.note}</p>

              {isCurrent ? (
                <button disabled className="mt-6 w-full bg-[#2A2A2A] text-[#A0A0A0] font-semibold py-3 text-sm" data-testid={`current-plan-${t.id}`}>
                  Current Plan
                </button>
              ) : (
                <button
                  onClick={() => onSubscribe(t.id)}
                  disabled={busyTier !== null}
                  data-testid={`subscribe-${t.id}`}
                  className={`mt-6 w-full inline-flex items-center justify-center gap-2 font-bold py-3 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${t.highlighted ? "bg-[#D4AF37] text-[#080808] hover:bg-[#F0C84B]" : "border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0]"}`}
                >
                  {busyTier === t.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</> : <>Subscribe <ArrowRight className="w-4 h-4" /></>}
                </button>
              )}

              <ul className="mt-6 space-y-2.5 border-t border-white/10 pt-6">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-xs md:text-sm text-[#F4F4F0]/90">
                    <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ---------- Pay-As-You-Go ---------- */
function PaygView({ pricing, busyPack, onBuyPack }) {
  const packs = (pricing?.packs || []).filter((p) => !p.admin_only);
  return (
    <div data-testid="payg-view">
      <div className="mb-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 flex items-start gap-4">
        <Coins className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
        <div className="flex-1">
          <h3 className="font-display text-lg font-bold text-[#F4F4F0]">Credits never expire.</h3>
          <p className="text-xs text-[#A0A0A0] mt-1 max-w-2xl">
            Buy a pack once, generate whenever. Failed renders auto-refund. Every quality tier (Draft / Pro / Cinema) burns from the same wallet. Larger packs = better $/credit.
          </p>
        </div>
        <Link to="/credits" className="hidden md:inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:underline whitespace-nowrap" data-testid="payg-wallet-link">
          View wallet →
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8" data-testid="credit-packs">
        {packs.map((p) => {
          const base    = Number(p.base_credits  ?? p.credits);
          const bonus   = Number(p.bonus_credits ?? 0);
          const total   = Number(p.credits);
          const popular = p.badge === "Most Popular";
          const best    = p.badge === "Best Value";
          return (
            <div key={p.sku} className={`relative border bg-[#121212] p-5 transition-colors ${
              popular ? "border-[#D4AF37] ring-1 ring-[#D4AF37]/40"
              : best  ? "border-emerald-500/60 ring-1 ring-emerald-500/30"
              : "border-[#2A2A2A] hover:border-[#D4AF37]/50"
            }`} data-testid={`pack-${p.sku}`}>
              {p.badge && (
                <span className={`absolute -top-3 left-4 text-[9px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 flex items-center gap-1 ${
                  popular ? "bg-[#D4AF37] text-[#080808]" : "bg-emerald-500 text-[#080808]"
                }`} data-testid={`pack-badge-${p.sku}`}>
                  {best && <Crown className="w-2.5 h-2.5" />} {p.badge}
                </span>
              )}
              <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">${p.price_usd}</p>
              <p className="font-display text-3xl font-black text-[#F4F4F0] mt-2">{total.toLocaleString()}</p>
              <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider mt-1">credits total</p>
              {bonus > 0 ? (
                <p className="text-[10px] text-[#D4AF37] mt-2" data-testid={`pack-bonus-${p.sku}`}>
                  {base.toLocaleString()} base <span className="text-[#5A5A5A]">+</span> <b>+{bonus.toLocaleString()} bonus</b>
                </p>
              ) : (
                <p className="text-[10px] text-[#5A5A5A] mt-2">{base.toLocaleString()} base · no bonus</p>
              )}
              <button
                onClick={() => onBuyPack(p.sku)}
                disabled={busyPack !== null}
                data-testid={`buy-pack-${p.sku}`}
                className={`mt-4 w-full font-bold text-xs py-2.5 transition-colors disabled:opacity-50 ${
                  popular || best ? "bg-[#D4AF37] text-[#080808] hover:bg-[#F0C84B]" : "border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0]"
                }`}
              >
                {busyPack === p.sku ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : `Buy $${p.price_usd}`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="border border-[#2A2A2A] bg-[#0F0F0F]">
        <div className="px-5 py-3 border-b border-[#2A2A2A]">
          <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0]">How credits map to quality</p>
        </div>
        <div className="grid grid-cols-4 px-5 py-2 border-b border-[#1A1A1A] text-[10px] uppercase tracking-wider text-[#5A5A5A]">
          <span>Feature</span><span className="text-center">Draft</span><span className="text-center">Pro</span><span className="text-center">Cinema</span>
        </div>
        {[
          { key: "image", label: "Image" },
          { key: "logo", label: "Logo" },
          { key: "voice_30s", label: "Voiceover · 30s" },
          { key: "voice_60s", label: "Voiceover · 60s" },
          { key: "video_10s", label: "Video · 10s" },
          { key: "video_20s", label: "Video · 20s" },
          { key: "video_30s", label: "Video · 30s" },
        ].map((it) => {
          const row = pricing?.matrix?.[it.key] || {};
          return (
            <div key={it.key} className="grid grid-cols-4 px-5 py-2.5 border-b border-[#1A1A1A] text-sm">
              <span className="text-[#F4F4F0]">{it.label}</span>
              {["draft", "pro", "cinema"].map((q) => (
                <span key={q} className="text-center font-mono text-[#A0A0A0]">{row[q] ?? "—"} cr</span>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Current plan banner ---------- */
function CurrentPlanCard({ user, usage, onOpenPortal, cancelling }) {
  const isPaid = !!usage.tier;
  const limit = usage.downloads_limit;
  const used = usage.downloads_used;
  const remaining = Math.max(0, limit - used);
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const window = usage.window === "week" ? "this week" : usage.window === "month" ? "this month" : "lifetime";
  const cancelled = usage.subscription_cancelled;
  const endDate = usage.subscription_expires_at
    ? new Date(usage.subscription_expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <div className="mb-8 border border-[#2A2A2A] bg-[#121212] p-6 md:p-7" data-testid="current-plan-card">
      <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between">
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#A0A0A0] mb-2">Your Plan</p>
          <p className="font-display text-2xl md:text-3xl font-extrabold text-[#F4F4F0]" data-testid="current-plan-label">{usage.tier_label}</p>
          <p className="text-sm text-[#A0A0A0] mt-2">
            <span className="text-[#D4AF37] font-bold">{remaining}</span> downloads left {window} · {used} of {limit} used
          </p>
          {isPaid && endDate && (
            <p className="text-xs text-[#A0A0A0] mt-1">
              {cancelled ? <span className="text-red-300">Cancelled — access ends {endDate}</span> : <>Renews {endDate}</>}
            </p>
          )}
        </div>
        {isPaid ? (
          <button
            onClick={onOpenPortal} disabled={cancelling}
            data-testid="manage-billing-button"
            className="bg-[#D4AF37] text-[#080808] font-bold px-6 py-3 hover:bg-[#F0C84B] transition-colors text-sm disabled:opacity-50 whitespace-nowrap"
          >
            {cancelling ? "Opening…" : "Manage Billing →"}
          </button>
        ) : (
          <Link to="/credits" className="border border-[#2A2A2A] hover:border-[#D4AF37] px-5 py-3 text-sm transition-colors">
            View Wallet →
          </Link>
        )}
      </div>
      <div className="mt-5 h-1 bg-[#2A2A2A] overflow-hidden">
        <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${pct}%` }} data-testid="usage-progress-bar" />
      </div>
    </div>
  );
}
